<template>
	<aside
		class="fixed flex flex-col top-[100px] left-10 h-[calc(100vh-120px)] border-[1px] border-black bg-white rounded-2xl isolate w-full max-w-[320px] max-md:max-w-[240px] transition-all duration-[800ms] overflow-x-hidden"
		:data-visible="props.isVisible"
	>
		<nav class="p-4">
			<template v-for="item in navItems" :key="item.label">
				<div v-if="!item.hidden" class="nav-item">
					<Button
						:variation="item.variation"
						style="justify-content: start !important"
						class="w-full"
						:class="{ 'nav-active': !item.subItems && isActive(item.path) }"
						@click="item.subItems ? toggleOpen(item) : navigateTo(item.path)"
						:disabled="item.disabled"
					>
						<Icon :name="item.icon" size="22" /> {{ item.label }}

						<Icon
							v-if="item.subItems"
							:name="!item.open ? 'mdi:chevron-right' : 'mdi:chevron-down'"
							size="22"
							class="ml-auto"
						/>
					</Button>

					<Transition name="layout-submenu">
						<div
							v-if="item.subItems && item.open"
							class="sub-items ps-6"
							:subItems-visible="item.open"
						>
							<template v-for="subItem in item.subItems">
								<Button
									v-if="!subItem.hidden"
									variation="secondary"
									class="w-full"
									:class="{ 'nav-active': isActive(subItem.path) }"
									style="justify-content: start !important"
									@click="navigateTo(subItem.path)"
									><Icon :name="subItem.icon" size="22" />{{ subItem.label }}</Button
								>
							</template>
						</div>
					</Transition>
				</div>
			</template>
		</nav>

		<div class="mt-auto p-4">
			<button
				class="text-gray-500 mb-2 text-sm font-bold"
				@click="handleActiveUsersClick"
			>
				Aktívni používatelia

				<Icon
					v-if="debugSocketStateStatus === 'pending'"
					name="eos-icons:loading"
				/>
			</button>

			<div
				v-for="user in activeUsers"
				:key="user.id"
				class="flex items-center gap-2 mb-1 last-of-type:mb-0 text-sm min-h-[22px]"
			>
				<div class="flex items-center gap-1">
					<div class="w-4 shrink-0 aspect-square grid place-items-center">
						<div
							v-show="!user.userBoxes?.length"
							class="w-2 h-2 rounded-full bg-green-500"
						></div>
						<Icon
							v-show="user.userBoxes?.length"
							name="mdi:pen"
							class="text-green-500"
						/>
					</div>
					<span>{{ user.name }}</span>
					<div
						v-if="user.role?.toLocaleLowerCase() === 'admin'"
						data-admin-badge
						class="pb-1"
					>
						<Icon name="clarity:crown-solid" size="18" class="text-yellow-500" />
					</div>
					<Icon
						v-if="user.isMaintainer"
						name="pajamas:issue-type-maintenance"
						size="14"
						class="text-blue-500"
					/>
				</div>
				<div class="ml-auto flex items-center gap-1 flex-wrap">
					<span
						v-for="product in user.userBoxes"
						:key="product.id"
						v-tooltip.top="
							`${product?.priceBox?.title} (${getFormattedDate(product.lockedAt, {
								timeStyle: 'short',
								dateStyle: 'short'
							})})`
						"
						class="text-[10px] border-[1px] border-gray-800 rounded-full px-1 font-bold"
					>
						{{ product.id }}
					</span>
				</div>
			</div>
		</div>
	</aside>
</template>

<script setup>
import useAuthStore from '~/store/auth'
import useSocketStore from '~/store/socket'
import { useMutation } from '@tanstack/vue-query'

const authStore = useAuthStore()
const socketStore = useSocketStore()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const isActive = (path) =>
	path === '/' ? route.path === '/' : !!path && route.path.startsWith(path)

const props = defineProps({
	isVisible: {
		type: Boolean
	}
})

const navItems = reactive([
	{
		label: 'Dashboard',
		icon: 'ic:baseline-space-dashboard',
		path: '/',
		variation: 'secondary'
	},
	{
		label: 'Cenové boxy',
		icon: 'ph:currency-dollar-fill',
		variation: 'secondary',
		open: true,
		subItems: [
			{
				label: 'Cenové boxy',
				icon: 'ph:list-bullets',
				path: '/boxes/list'
			},
			{
				label: 'Tipy',
				icon: 'ph:list-bullets',
				path: '/hints/list'
			},
			{
				label: 'Obchody',
				icon: 'ph:list-bullets',
				path: '/merchants/list'
			},
			{
				label: 'Staršie a zabudnuté',
				icon: 'mdi:archive-search-outline',
				path: '/boxes/review'
			},
			{
				label: 'Nahradiť',
				icon: 'codicon:replace-all',
				path: '/boxes/replace',
				hidden: !['admin', 'editor'].includes(authStore.role)
			}
		]
	},
	{
		label: 'Automatické kupóny',
		icon: 'mingcute:coupon-line',
		variation: 'secondary',
		path: '/coupon/list'
	},
	{
		label: 'Aliexpress link generátor',
		icon: 'arcticons:aliexpress',
		variation: 'secondary',
		path: '/tracking'
	},
	{
		hidden: authStore.role !== 'admin',
		label: 'Automatické preklady',
		icon: 'material-symbols:translate',
		variation: 'secondary',
		path: '/sites/list'
	},
	{
		hidden: authStore.role !== 'admin',
		label: 'Používatelia',
		icon: 'ic:baseline-supervisor-account',
		variation: 'secondary',
		path: '/users'
	},
	{
		label: 'Nastavenia',
		icon: 'majesticons:settings-cog',
		variation: 'secondary',
		path: '/settings'
	}
])

function toggleOpen(item) {
	item.open = !item.open
}

const { mutate: debugSocketState, status: debugSocketStateStatus } =
	useMutation({
		mutationFn: () => apiFetch('/socket-debug'),
		onSuccess: (debugData) => {
			console.table(debugData)
		},
		onError: (error) => {
			console.error(error)
		}
	})

const handleActiveUsersClick = () => {
	if (runtimeConfig.public.environment === 'production') {
		return
	}

	debugSocketState()
}

const activeUsers = computed(() =>
	socketStore.users.map((user) => {
		const isMaintainer = user.email?.includes('madelo.co')

		const userBoxes = socketStore.lockedPriceBoxes?.filter(
			(box) => box.userId === user.id
		)

		return { ...user, isMaintainer, userBoxes }
	})
)
</script>

<style scoped>
.nav-active {
	@apply !bg-boxy-50 !text-boxy-700;
	box-shadow: inset 3px 0 0 theme('colors.boxy.400');
}

[data-visible='false'] {
	transform: translateX(calc(-100% - 40px));
}
.slide-enter, .slide-leave-to /* start */ {
	transform: translateX(-100%);
}
.slide-enter-to, .slide-leave /* end */ {
	transform: translateX(0%);
}
.layout-submenu-enter-from,
.layout-submenu-leave-to {
	max-height: 0;
}

.layout-submenu-enter-to,
.layout-submenu-leave-from {
	max-height: 1000px;
}

.layout-submenu-leave-active {
	overflow: hidden;
	transition: max-height 0.45s cubic-bezier(0, 1, 0, 1);
}

.layout-submenu-enter-active {
	overflow: hidden;
	transition: max-height 1s ease-in-out;
}

[data-admin-badge] {
	display: inline-block;
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: -75%;
		width: 50%;
		height: 100%;
		background: linear-gradient(
			120deg,
			rgba(255, 255, 255, 0.5) 0%,
			rgba(255, 255, 255, 0) 100%
		);
		transform: skewX(-25deg);
		animation: shine 2s infinite;
		animation-delay: 10s;
	}
}

@keyframes shine {
	0% {
		left: -75%;
	}
	50% {
		left: 100%;
	}
	100% {
		left: 100%;
	}
}
</style>
